/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { IEditor } from "../IEditor";
import { IAccount, IResourceDescription } from "@adapptio/lib-core";
import { TEditorResource } from "../resources/ResourceTypes";
import { isResource, isResourceDescriptor } from "../resources/ResourceHelpers";
import { EnvironmentService } from "../environment/EnvironmentService";
import { offEvent, onEvent } from "@adapptio/lib-shared";
import { IIntegrationType } from "../integration/IntegrationServiceTypes";
import { isIntegrationType } from "../integration/integrationHelpers";

export enum ABILITY_ACTIONS {
	WRITE,
	READ,
	RENAME,
	REMOVE
}

export type TResourceSubject = TEditorResource | IResourceDescription;

export type TAction = ABILITY_ACTIONS;

export type TSubject = TResourceSubject | IIntegrationType;

export class AbilityService {
	private identity: IAccount;

	/** Service holding user data */
	private environmentSvc: EnvironmentService;

	constructor(private dc: IEditor) {
		this.environmentSvc = dc.get("environmentService");
	}

	public init(): void {
		this.reloadIdentity();
		this.subscribeDataUpdaters();
	}

	private reloadIdentity = () => {
		this.identity = this.environmentSvc.getIdentity();
	};

	private subscribeDataUpdaters(): void {
		onEvent(this.environmentSvc.onSessionChange, this.reloadIdentity);
	}

	private unsubscribeDataUpdaters(): void {
		offEvent(this.environmentSvc.onSessionChange, this.reloadIdentity);
	}

	private isAdmin(): boolean {
		return !!this.identity?.isAdmin;
	}

	public destroy(): void {
		this.unsubscribeDataUpdaters();
	}

	private checkResourceDescriptorAbility(action: ABILITY_ACTIONS, subject: IResourceDescription): boolean {
		switch (action) {
			case ABILITY_ACTIONS.READ:
				return this.isAdmin() || subject?.attributes?.hidden === false;
			case ABILITY_ACTIONS.WRITE:
				return subject?.attributes?.allowWrite === true;
			case ABILITY_ACTIONS.RENAME:
				return subject?.attributes?.allowRename === true;
			case ABILITY_ACTIONS.REMOVE:
				return subject?.attributes?.allowRemove === true;
			default:
				return false;
		}
	}

	private checkIntegrationTypeAbility(action: ABILITY_ACTIONS, subject: IIntegrationType): boolean {
		switch (action) {
			case ABILITY_ACTIONS.READ:
			case ABILITY_ACTIONS.WRITE:
			case ABILITY_ACTIONS.RENAME:
			case ABILITY_ACTIONS.REMOVE:
				return !subject.hidden;
			default:
				return false;
		}
	}

	can(action: TAction, subject: TSubject): boolean {
		if (isResource(subject)) {
			return this.checkResourceDescriptorAbility(action, subject.descriptor);
		}

		if (isResourceDescriptor(subject)) {
			return this.checkResourceDescriptorAbility(action, subject);
		}

		if (isIntegrationType(subject)) {
			return this.checkIntegrationTypeAbility(action, subject);
		}

		return false;
	}
}
